import React, { lazy, useEffect, useMemo } from 'react';

import { Outlet } from 'react-router';

import Footer from '../Footer';
import { HeroProvider } from '../HeroWrapper/HeroContext';

import useTheme from '@/components/ThemeWrapper/useTheme';
import { APP_STATUS_IDLE, BRAND_APPROVAL_STATUSES } from '@/config/common';
import { ROLES } from '@/config/user';
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks';
import { getIotStoreRedirectUrl } from '../Header/actions';
import * as Styled from './SignInLayout.style';

const Header = lazy(() => import(/* webpackChunkName: "Header" */ '../Header'));
const Hero = lazy(() => import(/* webpackChunkName: "Hero" */ '../Hero'));

export const SignInLayout = ({ status: statusProp, servicePartner: servicePartnerProp, userInfo: userInfoProp }) => {
  const { setTheme } = useTheme();
  const dispatch = useAppDispatch();

  const products = useAppSelector(state => state.shop?.products || { items: [] });
  const activeProAccounts = useAppSelector(state => state.app.activeProAccounts);
  const selectedSystem = useAppSelector(state => state.app.selectedSystem);
  const selectedUserInfo = useAppSelector(state => state.app.userInfo);
  const selectedServicePartner = useAppSelector(state => state.app.servicePartner);
  const selectedStatus = useAppSelector(state => state.app.status);
  const selectedLanguage = useAppSelector(state => state.app.selectedLanguage);

  const userInfo = userInfoProp ?? selectedUserInfo;
  const servicePartner = servicePartnerProp ?? selectedServicePartner;
  const status = statusProp ?? selectedStatus;

  useEffect(() => {
    document.body.classList.add('signed-in');

    const [mainBrand = {}] = servicePartner?.brands || [];
    setTheme(mainBrand?.brandId);

    return () => document.body.classList.remove('signed-in');
  }, [servicePartner, setTheme]);

  const spRoles = useMemo(() => userInfo?.spRoles || [], [userInfo?.spRoles]);
  const isAdmin = useMemo(() => spRoles.includes(ROLES.admin), [spRoles]);
  const subscription = useMemo(() => userInfo?.recurringPayment, [userInfo?.recurringPayment]);

  const isBuyerOrAdmin = useMemo(() => spRoles.includes(ROLES.admin) || spRoles.includes(ROLES.buyer), [spRoles]);

  const allBrandsApproved = useMemo(
    () =>
      Array.isArray(servicePartner?.brands) &&
      servicePartner?.brands.length > 0 &&
      servicePartner?.brands.some(brand => brand?.approvalStatus === BRAND_APPROVAL_STATUSES.APPROVED),
    [servicePartner?.brands],
  );

  const isPremiumButtonVisible = useMemo(
    () => isBuyerOrAdmin && allBrandsApproved && subscription && subscription.isValid === false,
    [isBuyerOrAdmin, allBrandsApproved, subscription],
  );

  return (
    <HeroProvider>
      <Styled.SignInLayout>
        {status?.statusCode?.value && status.statusCode.value !== APP_STATUS_IDLE && status?.statusText && (
          <Styled.StatusBanner role="status">{status.statusText}</Styled.StatusBanner>
        )}

        <Header
          isPremiumButtonVisible={isPremiumButtonVisible}
          isAdmin={isAdmin}
          userInfo={userInfo}
          servicePartner={servicePartner}
          products={products}
          activeProAccounts={activeProAccounts}
          selectedSystem={selectedSystem}
          getIotStoreRedirectUrl={() => dispatch(getIotStoreRedirectUrl())}
        />
        <Hero />

        <Styled.MainContent>
          <Outlet />
        </Styled.MainContent>

        <Footer
          subscription={subscription}
          isAdmin={isAdmin}
          isPremiumButtonVisible={isPremiumButtonVisible}
          isWelcome={false}
          languageSelected={selectedLanguage}
        />
      </Styled.SignInLayout>
    </HeroProvider>
  );
};

export default React.memo(SignInLayout);
